/**
 * A timeline item is an offset, not a clock time.
 *
 * `offsetMinutes` is signed minutes from `weddings.startTime`: the 2:00 PM
 * flat-lay against a 5:30 PM wedding is -210. Nothing stores the clock time it
 * renders as, which is the whole point — move the wedding's date and the
 * entire day moves with it, generated rows and hand-added rows alike, with no
 * stored value left disagreeing with the header.
 *
 * Moving the wedding's *hours* is a narrower promise. `durationMinutes` of
 * null means "runs until the next item starts, or until the wedding ends if
 * this is the last one." No path stores it by default any more:
 * `generateTimeline` fills every null before a row reaches the database, and
 * both add forms open at a real length rather than blank. A planner can still
 * ask for one explicitly — PATCH keeps `durationMinutes: null` as "make this
 * open-ended" on purpose — so the resolver still has to read it.
 *
 * The cost of filling it up front: a stored duration no longer retracks the
 * end time. Push the wedding's end back an hour and the last event stays the
 * length it was drafted at; the hour that opens up is a gap, not more dance.
 * A planner who wants the last event to reach the new end edits it. That
 * trade is what lets a later item be inserted after the last one without
 * stretching it, which is the whole point of EVE-62, and it is why
 * `generateTimeline` takes `hours` at all — once, at creation, to turn "run
 * to the end" into a number. Filling a mid-list null pays the same trade a
 * second time: a stored duration there no longer retracks to an item inserted
 * between it and the one after it, either.
 *
 * Explicit durations everywhere else keep gaps and overlaps representable.
 * Both are real: a ceremony ending at 6:30 before a 7:00 cocktail hour is a
 * gap, and photographs running through cocktails is an overlap. A model that
 * derived every duration from the next item's start could express neither.
 */
import {
  elapsedMinutes,
  formatTimeValue,
  minutesSinceMidnight,
  parseTimeOfDay,
  timeOfDayFromMinutes,
} from './wedding-hours';

export interface TimelineItemTiming {
  offsetMinutes: number;
  /** Null: runs to the next item, or to the wedding's end when last. No path
   *  stores it by default since EVE-62 — only a planner asking for an
   *  open-ended item outright does. A template row may still carry it, where
   *  `generateTimeline` fills it in rather than reading it as open-ended. */
  durationMinutes: number | null;
}

export interface TimelineTemplateItem extends TimelineItemTiming {
  label: string;
  note?: string | null;
}

/**
 * The planner's own ladder, read off the clock times in their source document.
 *
 * Their stated durations and their clock times disagree — "+30 mins, 7:00 PM"
 * after a 6:00 PM ceremony, "+5 hrs, 8:00 PM" after a 7:30 PM transition — and
 * the clock column is the one that forms a consistent ladder, so it is the one
 * the offsets come from. Durations are taken where the two agree and set to 30
 * where they do not. Transition is a real item, confirmed with the planner,
 * not merely the gap between cocktails and dinner.
 *
 * Cocktail Hour is the one row that does not follow that clock column. The
 * source document put it half an hour after the ceremony ended and gave it 30
 * minutes, which drafts a day where the guests stand around after the vows and
 * then again before dinner. It starts when the ceremony ends and runs the hour
 * its name claims, closing both gaps against Transition at +120.
 *
 * `Dance` keeps a null duration, and in a template that now means "fill the
 * remaining night when this is drafted" rather than "stay open-ended". A fixed
 * number here and a draft that fits the hours are mutually exclusive, and the
 * hours are what vary: the same ladder has to draft a six-hour wedding and an
 * eight-hour one. `generateTimeline` turns it into a length; nothing stores it.
 */
export const DEFAULT_TIMELINE_TEMPLATE: readonly TimelineTemplateItem[] = [
  { label: 'Establishing & Detail Flat Lay Photos', offsetMinutes: -210, durationMinutes: 30 },
  { label: 'Getting Ready Photos', offsetMinutes: -180, durationMinutes: 30 },
  { label: 'Bride in Dress', offsetMinutes: -120, durationMinutes: 15 },
  { label: 'B&G First Look', offsetMinutes: -90, durationMinutes: 30 },
  { label: 'Wedding Party Photos', offsetMinutes: -60, durationMinutes: 30 },
  { label: 'Family Photos', offsetMinutes: -30, durationMinutes: 30 },
  { label: 'Guest Arrival', offsetMinutes: 0, durationMinutes: 30 },
  { label: 'Ceremony', offsetMinutes: 30, durationMinutes: 30 },
  { label: 'Cocktail Hour', offsetMinutes: 60, durationMinutes: 60 },
  { label: 'Transition', offsetMinutes: 120, durationMinutes: 30 },
  { label: 'Dinner', offsetMinutes: 150, durationMinutes: 90 },
  { label: 'Dance', offsetMinutes: 240, durationMinutes: null },
];

/**
 * How long a trailing item runs when the night's length cannot answer.
 *
 * Reached two ways, both of them a template and a wedding disagreeing: the
 * planner has not said when the night finishes, or the template's last item
 * starts after it already has. Either way there is no remaining night to
 * measure, and the item still needs a length, because the one thing it must
 * not be is open-ended.
 *
 * Two hours is a dance set — the row this most often lands on. It is not
 * `DRAFT_DURATION_MINUTES` (30, in `TimelineAxis`) for the reason that one is
 * not `DEFAULT_WINDOW_MINUTES`: half an hour is what a planner clicks into an
 * empty gap, and the last thing on a wedding night is not that.
 */
export const TRAILING_FALLBACK_MINUTES = 120;

/**
 * Minutes from an offset to the end of the night, or null when the question has
 * no answer: the planner has not said when the night finishes, or the offset is
 * already past it. Zero is an answer — an item starting exactly at the end is a
 * point in time, not an overrun.
 *
 * Shared by `generateTimeline`'s trailing fill and `resolveTimeline`'s trailing
 * read, which are the same question asked at two different times — once when a
 * null is written, once when one that was left unwritten is read. A comment
 * asserting the two agree is not how that gets enforced; one function is.
 */
function minutesToNightEnd(span: number | null, offsetMinutes: number): number | null {
  if (span === null) return null;
  const toEnd = span - offsetMinutes;
  return toEnd >= 0 ? toEnd : null;
}

/** What `generateTimeline` hands back: every row, with every duration a number. */
export type GeneratedTimelineItem = TimelineTemplateItem & { durationMinutes: number };

/**
 * The rows to insert for a new wedding.
 *
 * Offsets are already relative, so nothing here is arithmetic against the
 * wedding's date — move the date and every row moves with it. The last item
 * used to store `null` outright, which is what let it absorb slack: inserting
 * anything after it stretched the trailing block rather than leaving it where
 * the planner put it. Every null is filled here instead, including one found
 * anywhere in the template, and a trailing duration the template *states* is
 * left alone rather than being overwritten the way it used to be.
 *
 * A null in the middle fills to its next item's start — `next.offsetMinutes -
 * row.offsetMinutes`, the same rule `resolveTimeline` applies when it meets an
 * unfilled one at read time. That lands on zero, not a negative number, only
 * when two rows share an offset, and zero there is a real answer, a moment,
 * not a bug to go chase. The trailing null fills with `minutesToNightEnd(span,
 * …) ?? TRAILING_FALLBACK_MINUTES` — a template whose last item starts exactly
 * when the night ends still yields a zero-length item; one starting after the
 * end now carries the fallback rather than no length at all, and
 * `endsAfterWedding` flagged it before this change and flags it still.
 *
 * Copies every row, not just the array. Callers insert these into the database
 * by stamping a `weddingId` onto each one, and a shallow copy of the array
 * would hand out the module constant's own objects — the first mutating caller
 * would corrupt `DEFAULT_TIMELINE_TEMPLATE` for the rest of the process, and
 * the next wedding created would inherit the damage. The fill above is a
 * second reason now that it assigns in place: without the copy, filling one
 * wedding's null would write a number onto `DEFAULT_TIMELINE_TEMPLATE`'s own
 * `Dance` row, and the next wedding generated would read that number back as
 * if the planner had stated it.
 */
export function generateTimeline(
  template: readonly TimelineTemplateItem[],
  hours: { startTime: string | null; endTime: string | null },
): GeneratedTimelineItem[] {
  const rows = template.map((r) => ({ ...r })).sort((a, b) => a.offsetMinutes - b.offsetMinutes);
  const span = elapsedMinutes(hours.startTime, hours.endTime);

  rows.forEach((row, i) => {
    if (row.durationMinutes === null) {
      const next = rows[i + 1];
      row.durationMinutes = next
        ? next.offsetMinutes - row.offsetMinutes
        : minutesToNightEnd(span, row.offsetMinutes) ?? TRAILING_FALLBACK_MINUTES;
    }
  });

  // Every row's durationMinutes was null or a number going in, and every null
  // was just assigned a number above — the cast is where that loop invariant
  // becomes the declared return type.
  return rows as GeneratedTimelineItem[];
}

export interface ResolvedTimelineItem<T extends TimelineItemTiming> {
  item: T;
  /** `'HH:MM'` wall-clock at the venue. */
  clockTime: string;
  /**
   * Calendar days from the wedding's own date — -2 through 2 for any offset the
   * add form can produce. Nothing here bounds it; a larger offset yields a
   * larger number.
   */
  dayOffset: number;
  /**
   * Null when the item is last, has no stored duration, and either the
   * wedding has no end time or the item starts after it — there is no
   * positive length to report either way. An explicit duration always
   * resolves regardless of position.
   *
   * **0 and null are different answers, so test `=== null` rather than
   * falsiness.** 0 is a point in time — the send-off at the exact moment the
   * night ends, which is an ordinary thing for a planner to schedule. Null is
   * open-ended. A falsy check collapses the two and renders the ordinary case
   * as the broken one; note that `frontend/src/lib/time-input.ts` already
   * carries exactly that idiom (`gap ? formatDuration(gap) : undefined`) for
   * its own purposes, so it is the wrong thing to copy here.
   */
  resolvedDurationMinutes: number | null;
  /**
   * The item is still running after the wedding is over — computed from its
   * resolved end (`offsetMinutes + resolvedDurationMinutes`), not just its
   * start. Starting late and running long are the same problem to a planner:
   * either way the item needs the wedding to be longer than it is. An item
   * that starts exactly at the end and has nowhere further to run resolves
   * to a zero-length point in time, not an overrun. Rendered, flagged, never
   * dropped.
   */
  endsAfterWedding: boolean;
  /**
   * Where the item finishes, as a wall clock — or null when it does not.
   *
   * Derived here rather than by whoever draws it, for the same reason
   * `clockTime` is: the wrap past midnight is one rule, and a second
   * implementation of it is a second chance to get 1:00 AM wrong. A block
   * saying "9:00 PM – 1:00 AM" must agree with the block below it about what
   * one o'clock means.
   *
   * Null exactly when `resolvedDurationMinutes` is: an open-ended item has no
   * end to name, and one starting after the night is over has no length to
   * measure. A zero-length item ends when it starts, which is a real answer
   * and not a missing one.
   */
  endClockTime: string | null;
  /** Calendar days from the wedding's own date for `endClockTime`, or null with it. */
  endDayOffset: number | null;
}

/**
 * Offsets in, everything the UI renders out.
 *
 * The client never recomputes any of this. Two implementations of the wrap rule
 * is one more than the number that can be right.
 */
export function resolveTimeline<T extends TimelineItemTiming>(
  items: readonly T[],
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): ResolvedTimelineItem<T>[] {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  // An offset with nothing to be an offset from is not a time. Returning an
  // empty list rather than guessing an anchor is what lets the UI ask for one.
  if (!start) return [];

  const startMinutes = minutesSinceMidnight(start);
  // Minutes from the start to the end, wrapping past midnight. Null when the
  // planner has not said when the night finishes.
  const span = elapsedMinutes(startTime, endTime);

  const sorted = [...items].sort((a, b) => a.offsetMinutes - b.offsetMinutes);

  return sorted.map((item, i) => {
    const absolute = startMinutes + item.offsetMinutes;
    const next = sorted[i + 1];

    let resolved: number | null;
    if (item.durationMinutes !== null) {
      resolved = item.durationMinutes;
    } else if (next) {
      resolved = next.offsetMinutes - item.offsetMinutes;
    } else {
      // Zero is a real answer: an item starting exactly at the end is a
      // point in time, not an overrun, and not the open-ended "runs to the
      // end of the night" that null means. Only a negative toEnd — starting
      // after the end — has nothing to report, and neither does a wedding
      // with no end time at all.
      resolved = minutesToNightEnd(span, item.offsetMinutes);
    }

    return {
      item,
      clockTime: formatTimeValue(timeOfDayFromMinutes(absolute)),
      // Floor, not truncation: a -90 absolute is the previous day, and
      // `Math.trunc` would call it day zero.
      dayOffset: Math.floor(absolute / 1440),
      resolvedDurationMinutes: resolved,
      endsAfterWedding: span !== null && item.offsetMinutes + (resolved ?? 0) > span,
      endClockTime:
        resolved === null ? null : formatTimeValue(timeOfDayFromMinutes(absolute + resolved)),
      endDayOffset: resolved === null ? null : Math.floor((absolute + resolved) / 1440),
    };
  });
}

/** `'17:30'` and an offset of -210 → `'14:00'`. The inverse is `offsetForClockTime`. */
export function clockTimeForOffset(
  startTime: string | null | undefined,
  offsetMinutes: number,
): string | null {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  if (!start) return null;
  return formatTimeValue(timeOfDayFromMinutes(minutesSinceMidnight(start) + offsetMinutes));
}

/**
 * A time earlier in the day than the start is either this morning's prep or
 * tonight's small hours, and twelve hours is where one becomes the other:
 * against a 5:30 PM wedding, 5:30 AM is the tail of the night and 6:00 AM is
 * the morning before it. Both readings are ordinary; nothing beyond this is.
 */
const SMALL_HOURS_CUTOFF_MINUTES = 12 * 60;

/**
 * What the planner typed, as an offset.
 *
 * A planner thinks "4:15," not "minus seventy-five," so the add and edit forms
 * take a clock time and this converts it. Times at or after the start are read
 * as the same day and times before it as earlier the same day — a 2:00 PM entry
 * against a 5:30 PM wedding is -210, not +1290 — unless the gap crosses
 * `SMALL_HOURS_CUTOFF_MINUTES`, at which point it reads as the small hours of
 * the night that just started instead.
 */
export function offsetForClockTime(
  startTime: string | null | undefined,
  clockTime: string,
): number | null {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  const target = parseTimeOfDay(clockTime);
  if (!start || !target) return null;
  const diff = minutesSinceMidnight(target) - minutesSinceMidnight(start);
  if (diff < 0) return diff + 1440 <= SMALL_HOURS_CUTOFF_MINUTES ? diff + 1440 : diff;
  return diff;
}
